import { TIME_PATTERN, YEAR_MONTH_DAY_PATTERN } from 'modules/common/constants/date-range';
import { formatDate } from 'modules/dashboard/functions';
import * as XLSX from 'xlsx-js-style';
import powerBIVisualPresets from '../../../dashboard/components/tab-container/export-visual-data/presets';
import { BORDERS, EXCEL_COLORS } from './style';

/**
 * Converts a 2D array of spreadsheet-like objects into a plain string table.
 * Each row is joined with newline, and columns are aligned using spaces.
 * Ignores all styling metadata and only uses the `v` value.
 *
 * @param {Array<Array<{v?: any}>>} data - 2D array of cell objects
 * @returns {string} Formatted plain text table
 */
export const toStringTable = (data) => {
  if (!Array.isArray(data) || data.length === 0) return '';

  // convert to raw matrix
  const matrix = data.map((row) => row.map((cell) => cell?.v ?? '').map(String));

  // compute column widths
  const colWidths = [];
  matrix.forEach((row) => {
    row.forEach((cell, i) => {
      colWidths[i] = Math.max(colWidths[i] || 0, cell.length);
    });
  });

  const pad = (str, len) => str + ' '.repeat(len - str.length);

  // build rows
  const lines = matrix.map((row) => row.map((cell, i) => pad(cell, colWidths[i])).join(' | '));

  return lines.join('\n');
};

/**
 * Returns the number of columns from a given reference.
 *
 * @param {string} ref - The reference string to extract column information from.
 * @returns {number} The number of columns.
 */
export function getNumberOfColumns(ref) {
  // find the string portion of the column reference
  const match = ref.match(/[A-Z]+/g);
  const column = match[1];

  // supports upto 702 columns
  let noCols = column.charCodeAt(0) - 64;
  if (column.length === 2) {
    noCols = 26 * (column.charCodeAt(0) - 64) + (column.charCodeAt(1) - 64);
  }

  return noCols;
}

/**
 * Converts a spreadsheet-style column label (e.g., "A", "B", ..., "AA") into a zero-based index.
 *
 * @param {string} str - The input string that may contain a column label (e.g., "A", "AA12").
 *                       Only letters A-Z are considered; all others are ignored.
 * @returns {number} The zero-based column index (e.g., "A" → 0, "B" → 1, "AA" → 26).
 *
 */
function columnIndexFromRow(str) {
  // Extract only A-Z letters (uppercase them for consistency)
  const letters = str.toUpperCase().replace(/[^A-Z]/g, '');

  let result = 0;
  for (let i = 0; i < letters.length; i += 1) {
    const charCode = letters.charCodeAt(i) - 65; // 'A' -> 0
    result = result * 26 + charCode;
  }
  return result;
}

/**
 * Calculates the optimal column widths for a spreadsheet sheet
 * based on the length of the cell values.
 *
 * @param {Object} sheet - The sheet object (e.g., from SheetJS) where keys are cell addresses (e.g., "A1", "B2").
 * @param {number} noOfColumns - The total number of columns to consider.
 * @returns {number[]} An array of column widths, where each entry corresponds
 *                     to the maximum length of the values in that column.
 *
 */
export function findColumnWidths(sheet, noOfColumns) {
  const widths = new Array(noOfColumns).fill(10);
  Object.keys(sheet).forEach((row) => {
    if (row.includes('!')) {
      return null;
    }
    const value = sheet[row]?.v;
    const valueLength = `${value}`.length;
    const columnIndex = columnIndexFromRow(row);
    if (widths[columnIndex] < valueLength) {
      widths[columnIndex] = valueLength;
    }
    return row;
  });
  return widths;
}

/**
 * Exports and triggers download of an Excel file from the given CSV data.
 *
 * @param {string} fileName - The name of the Excel file to be downloaded.
 * @param {string} csv - The CSV data as a string to be converted into Excel format.
 * @param {Array} mergeCellMap - An array specifying cell ranges to be merged in the Excel sheet.
 */
export function exportExcel(fileName, csv, mergeCellMap, tableStartPos) {
  const sheet = XLSX.utils.aoa_to_sheet(csv);
  const noOfColumns = getNumberOfColumns(sheet['!ref']);

  const widths = findColumnWidths(sheet, noOfColumns);

  sheet['!merges'] = mergeCellMap;

  // create the !cols field if missing on the sheet
  if (!sheet['!cols']) sheet['!cols'] = [];

  sheet['!cols'] = widths.map((width) => ({ wch: width }));

  const range = XLSX.utils.decode_range(sheet['!ref']);
  const lastRow = range.e.r;
  for (let col = range.s.c; col <= range.e.c; col += 1) {
    const cellAddress = XLSX.utils.encode_cell({ r: lastRow, c: col });
    if (!sheet[cellAddress]) {
      sheet[cellAddress] = { v: '', t: 's' };
    }
    sheet[cellAddress].s = {
      border: BORDERS.TOP_BOTTOM_RIGHT,
    };
  }

  // make sure the 1st column always in string data type
  for (let row = range.s.r; row <= range.e.r; row += 1) {
    const cellAddress = XLSX.utils.encode_cell({ r: row, c: 0 });
    const cell = sheet[cellAddress];

    if (!cell) {
      sheet[cellAddress] = { v: '', t: 's' };
    } else {
      cell.v = String(cell.v ?? '');
      cell.t = 's';
    }
  }

  const updatedSheet = {};
  Object.keys(sheet).forEach((cell) => {
    const rowNo = parseInt(cell.match(/\d+$/), 10);
    if (rowNo % 2 === 0 && tableStartPos < rowNo && !sheet[cell]?.s?.fill?.fgColor) {
      updatedSheet[cell] = {
        ...sheet[cell],
        s: {
          ...sheet[cell].s,
          fill: {
            patternType: 'solid',
            fgColor: { rgb: EXCEL_COLORS.ROW_BG },
          },
        },
      };
    } else {
      updatedSheet[cell] = sheet[cell];
    }
  });

  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, updatedSheet, 'Sheet1');

  XLSX.writeFile(workbook, `${fileName}_${formatDate(new Date(), YEAR_MONTH_DAY_PATTERN)}.xlsx`);
}

/**
 * Creates metadata header rows for an Excel sheet including username, hotel name, and generation date.
 *
 * @param {string} hotelName - The name of the hotel to include in the metadata.
 * @param {string} user - The username to include in the metadata.
 * @returns {Array<Array<{ v: string, t: 's', s?: { font?: { bold?: boolean } } }>>}
 *          An array of rows, where each row is an array of styled cell objects.
 */
export function addMetaHeaders(
  hotelName,
  user,
  comparisonHotelName = null,
  isInterpreterAgent = false
) {
  return [
    !isInterpreterAgent && [
      { v: 'Username', t: 's', s: { font: { bold: true } } },
      { v: user, t: 's' },
    ],
    !isInterpreterAgent && [
      { v: 'Report Generation Date', t: 's', s: { font: { bold: true } } },
      {
        v: `${formatDate(new Date(), YEAR_MONTH_DAY_PATTERN)}, ${formatDate(
          new Date(),
          TIME_PATTERN
        )}`,
        t: 's',
      },
    ],
    ...(comparisonHotelName
      ? [
          [
            { v: 'Selection Hotel (A)', t: 's', s: { font: { bold: true } } },
            { v: hotelName, t: 's' },
          ],
          [
            { v: 'Comparison Hotel (B)', t: 's', s: { font: { bold: true } } },
            { v: comparisonHotelName, t: 's' },
          ],
        ]
      : [
          [
            { v: 'Hotel Name', t: 's', s: { font: { bold: true } } },
            { v: hotelName, t: 's' },
          ],
        ]),
  ].filter(Boolean);
}

/**
 * Formats a value for an Excel cell with appropriate style based on type and value.
 *
 * - Numbers are styled with conditional red font for negatives.
 * - Percent strings are parsed and styled with percentage format and red if negative.
 * - `undefined` returns a plain styled cell without a value.
 *
 * @param {*} value - The value to be formatted (number, string with %, or undefined).
 * @param {boolean} [isBold=false] - Whether to render the text in bold.
 * @returns {Object} A cell object formatted for use in Excel (e.g., with xlsx-js-style).
 */
export function formatNumerals(value, isBold = false) {
  let attr = { s: { border: BORDERS.TOP_RIGHT, font: { bold: isBold } } };
  if (value === undefined) return attr;
  if (typeof value === 'number')
    attr = {
      t: 'n',
      v: value,
      s: {
        font: { color: { rgb: value < 0 ? 'FF0000' : '000000' }, bold: isBold },
        border: BORDERS.TOP_RIGHT,
      },
    };

  if (typeof value === 'string') {
    // this is to handle text fields with %, but it is not actually a string
    // eg: rate plan visual has rate names like  `Promotion 20% off`
    const matchLetters = /[a-zA-Z]/g;
    const valueContainsLetters = matchLetters.test(value);
    if (value.includes('%')) {
      if (valueContainsLetters) {
        attr = {
          v: value,
          t: 'string',
          s: {
            font: { color: { rgb: value < 0 ? 'FF0000' : '000000' }, bold: isBold },
            border: BORDERS.TOP_RIGHT,
          },
        };
      } else {
        value = parseFloat(value.replace(',', '').replace('%', '')) / 100.0;
        attr = {
          v: value,
          t: 'n',
          z: '#,##0.00%',
          s: {
            font: { color: { rgb: value < 0 ? 'FF0000' : '000000' }, bold: isBold },
            border: BORDERS.TOP_RIGHT,
          },
        };
      }
    } else {
      // NOTE: make sure the value is a float which does not contain any letters, but can contains . and ,
      // eslint-disable-next-line no-lonely-if
      if (!valueContainsLetters && !Number.isNaN(parseFloat(value.replaceAll(',', '')))) {
        attr = {
          v: value.replaceAll(',', ''),
          t: 'n',
          z: '#,##0.00',
          s: {
            font: { color: { rgb: value < 0 ? 'FF0000' : '000000' }, bold: isBold },
            border: BORDERS.TOP_RIGHT,
          },
        };
        if (value.includes('.')) {
          attr = {
            ...attr,
            z: '#,##0.00',
          };
        } else {
          attr = {
            ...attr,
            z: '0.00',
          };
        }
      }
    }
  } else {
    // eslint-disable-next-line no-lonely-if
    if (typeof value === 'number') {
      // check if the number is a float
      if (Number.isFinite(value) && !Number.isInteger(value)) {
        attr = {
          ...attr,
          z: '#,##0.00',
        };
      }
    }
  }

  return attr;
}

/**
 * Generates an Excel file based on the provided tabular data and returns the generated file content as a string.
 *
 * @param {string} fileName - The name of the Excel file to be generated.
 * @param {Object} [headerFiltersInfo] - Optional metadata to render a filter header section.
 * @param {string} [headerFiltersInfo.title] - Title to be displayed in the filter header.
 * @param {string} [headerFiltersInfo.hotelName] - Hotel name to display alongside the title.
 * @param {Array<Object>} [headerFiltersInfo.sections] - Additional filter sections for the filter header.
 * @param {Array<Array<Object>>} headerRows - An array of rows representing the Excel header section.
 * @param {Array<Object>} flatColumns - The flattened column definitions used to map row data.
 * @param {Array<Object>} rowData - An array of row data to be included in the Excel sheet.
 * @param {Object} totalData - An object representing the final row (typically total or summary).
 * @param {{ username: string }} user - An object containing user information (only `username` is required).
 * @param {{isInterpreterAgent: boolean}} - Indicates whether the request is from an interpreter agent.
 *
 * @returns {string} Build excel file content.
 */
export function buildExcelContent({
  fileName,
  headerFiltersInfo,
  headerRows,
  flatColumns,
  rowData,
  user,
  isInterpreterAgent = false,
}) {
  const csv = [];
  const mergeCellMap = []; // map to record which cell should be merged together horizontally

  const metaData = addMetaHeaders(
    headerFiltersInfo?.hotelName,
    user?.username,
    headerFiltersInfo?.comparisonHotelName,
    isInterpreterAgent
  );
  csv.push(...metaData);

  // Add filter headers
  headerFiltersInfo.sections.forEach((section) => {
    csv.push([
      {
        v: section.title,
        t: 's',
        s: { font: { bold: true } },
      },
      { v: section.value, t: 's' },
    ]);
  });

  // leave a gap between the headers
  csv.push([]);
  csv.push([]);

  // Add table headers
  headerRows.forEach((row) => {
    const cells = row.map((cell) => {
      if (cell?.children !== undefined) {
        // add blank cell to add space to merge
        const blankCells = cell?.children
          // eslint-disable-next-line arrow-body-style
          .map((c, i) => {
            return {
              t: 's',
              v: '',
              s: {
                border: i === cell.children.length - 1 ? BORDERS.TOP_RIGHT : BORDERS.TOP,
                fill: {
                  patternType: 'solid',
                  fgColor: { rgb: EXCEL_COLORS.HEADER_BG },
                },
              },
            };
          })
          // remove one blank to occupy the correct label
          .splice(1);

        return [
          {
            v: cell?.label,
            t: 's',
            s: {
              border: BORDERS.TOP,
              fill: {
                patternType: 'solid',
                fgColor: { rgb: EXCEL_COLORS.HEADER_BG },
              },
              font: { bold: true, color: { rgb: EXCEL_COLORS.HEADER_TEXT, sz: '10' } },
            },
          },
          ...blankCells,
        ];
      }
      // Sub header columns without children
      return {
        v: cell.label,
        t: 's',
        s: {
          border: BORDERS.TOP_RIGHT,
          fill: {
            patternType: 'solid',
            fgColor: { rgb: EXCEL_COLORS.HEADER_BG },
          },
          font: { bold: true, color: { rgb: EXCEL_COLORS.HEADER_TEXT, sz: '10' } },
        },
      };
    });

    csv.push(cells.flat());
  });

  // Add table data
  rowData.forEach((data) => {
    const row = flatColumns.map((col) => ({
      v: data[col?.id] || '',
      ...formatNumerals(data[col?.id]),
    }));
    csv.push(row);
  });
  //
  if (fileName) {
    const tableStartPos = powerBIVisualPresets[fileName]?.table?.start;
    return { csv, mergeCellMap, tableStartPos };
  }
  return { csv, mergeCellMap };
}

/**
 * Generates an Excel file based on the provided tabular data and returns the generated file content as a string.
 *
 * @param {string} fileName - The name of the Excel file to be generated.
 * @param {Object} [headerFiltersInfo] - Optional metadata to render a filter header section.
 * @param {string} [headerFiltersInfo.title] - Title to be displayed in the filter header.
 * @param {string} [headerFiltersInfo.hotelName] - Hotel name to display alongside the title.
 * @param {Array<Object>} [headerFiltersInfo.sections] - Additional filter sections for the filter header.
 * @param {Array<Array<Object>>} headerRows - An array of rows representing the Excel header section.
 * @param {Array<Object>} flatColumns - The flattened column definitions used to map row data.
 * @param {Array<Object>} rowData - An array of row data to be included in the Excel sheet.
 * @param {Object} totalData - An object representing the final row (typically total or summary).
 * @param {{ username: string }} user - An object containing user information (only `username` is required).
 *
 * @returns {string} The generated Excel file content as a string (e.g. base64 or downloadable blob reference).
 */
export default function generateExcel(
  fileName,
  headerFiltersInfo,
  headerRows,
  flatColumns,
  rowData,
  user
) {
  const { csv, mergeCellMap, tableStartPos } = buildExcelContent({
    fileName,
    headerFiltersInfo,
    headerRows,
    flatColumns,
    rowData,
    user,
  });
  //
  const excel = exportExcel(fileName, csv, mergeCellMap, tableStartPos);
  //
  return excel;
}
